import {Activity, ActivityFeatureOption} from '@activities/models/activities.types';
import {monthLabel, parsePeriodKey, periodKeyForMonth} from '@vat/engine/month';
import {round2} from '@vat/engine/rounding';
import type {LandlordTaxProfile, VatAdjustment, VatEngineResult, VatKz, VatLine, VatPeriodType, VatReturn, VatReturnStatus} from '@vat/engine/types';
import {VAT_CORRECTION_COPY} from '../vat-copy';

/**
 * Reine Helfer des Status-Workflows (Spec 8): erlaubte Übergänge, lines-Chunks (≤ 400 je Dokument),
 * Diff über line.id für die Änderungs-Erkennung, § 17-Nachträge, Versionslogik der Berichtigung,
 * Activity-Fabrik. Kein Angular, kein Firebase.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export const VAT_TRANSITIONS: Readonly<Record<VatReturnStatus, readonly VatReturnStatus[]>> = {
    ENTWURF: ['GEPRUEFT'],
    GEPRUEFT: ['ENTWURF', 'EXPORTIERT', 'BERICHTIGT'],
    EXPORTIERT: ['BERICHTIGT'],
    BERICHTIGT: ['GEPRUEFT', 'EXPORTIERT'],   // nur die Rücknahme einer Berichtigung zurück auf vorherigerStatus
};

export const canTransition = (from: VatReturnStatus, to: VatReturnStatus): boolean =>
    (VAT_TRANSITIONS[from] ?? []).includes(to);

// --- lines-Chunks ------------------------------------------------------------

export const LINES_CHUNK_SIZE = 400;

export interface VatLinesChunk {
    index: number;
    lines: VatLine[];
}

export const chunkLines = (lines: VatLine[], size: number = LINES_CHUNK_SIZE): VatLinesChunk[] => {
    const chunks: VatLinesChunk[] = [];
    for (let start = 0, index = 0; start < lines.length; start += size, index += 1) {
        chunks.push({index, lines: lines.slice(start, start + size)});
    }
    return chunks;
};

export const chunkDocID = (index: number): string => `chunk_${String(index).padStart(3, '0')}`;

export const flattenChunks = (chunks: VatLinesChunk[]): VatLine[] =>
    [...chunks].sort((a, b) => a.index - b.index).flatMap(chunk => chunk.lines ?? []);

// --- Diff --------------------------------------------------------------------

export type VatLineDiffKind = 'ADDED' | 'REMOVED' | 'CHANGED';

export interface VatLineDiffEntry {
    id: string;
    kind: VatLineDiffKind;
    kz: VatKz;
    prev: VatLine | null;
    next: VatLine | null;
    deltaGross: number;
    deltaTax: number;
}

export interface VatLinesDiff {
    entries: VatLineDiffEntry[];
    deltaGross: number;
    deltaTax: number;
    deltaByKz: Partial<Record<VatKz, { gross: number; tax: number }>>;
}

export const linesEqual = (a: VatLine, b: VatLine): boolean =>
    a.kz === b.kz && a.rule === b.rule && a.gross === b.gross && a.net === b.net && a.tax === b.tax
    && a.share === b.share && a.monthKey === b.monthKey && a.category === b.category && a.rate === b.rate;

export const diffLines = (prev: VatLine[], next: VatLine[]): VatLinesDiff => {
    const prevByID = new Map(prev.map(line => [line.id, line]));
    const nextByID = new Map(next.map(line => [line.id, line]));
    const entries: VatLineDiffEntry[] = [];
    for (const id of new Set([...prevByID.keys(), ...nextByID.keys()])) {
        const p = prevByID.get(id) ?? null;
        const n = nextByID.get(id) ?? null;
        if (p && n && linesEqual(p, n)) {
            continue;
        }
        entries.push({
            id,
            kind: !p ? 'ADDED' : !n ? 'REMOVED' : 'CHANGED',
            kz: (n ?? p).kz,
            prev: p,
            next: n,
            deltaGross: round2((n?.gross ?? 0) - (p?.gross ?? 0)),
            deltaTax: round2((n?.tax ?? 0) - (p?.tax ?? 0)),
        });
    }
    entries.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const deltaByKz: VatLinesDiff['deltaByKz'] = {};
    for (const entry of entries) {
        const current = deltaByKz[entry.kz] ?? {gross: 0, tax: 0};
        deltaByKz[entry.kz] = {gross: round2(current.gross + entry.deltaGross), tax: round2(current.tax + entry.deltaTax)};
    }
    return {
        entries,
        deltaGross: round2(entries.reduce((sum, entry) => sum + entry.deltaGross, 0)),
        deltaTax: round2(entries.reduce((sum, entry) => sum + entry.deltaTax, 0)),
        deltaByKz,
    };
};

// --- § 17-Nachtrag -----------------------------------------------------------

export interface NachtragMeta {
    uid: string;
    displayName: string;
    now: Date;
    sourceReturnID: string;
}

const KIND_LABEL: Record<VatLineDiffKind, string> = {ADDED: 'neu', REMOVED: 'entfallen', CHANGED: 'geändert'};

/** Je Diff-Eintrag ein NACHTRAG_17 auf die Zeile; Vorsteuer-Zeilen tragen das Steuer-Delta (Plan 1, Task 15). */
export const buildNachtrag17Adjustments = (diff: VatLinesDiff, meta: NachtragMeta): VatAdjustment[] =>
    diff.entries
        .filter(entry => entry.kz !== 'NONE')
        .map(entry => ({entry, value: entry.kz === '66' ? entry.deltaTax : entry.deltaGross}))
        .filter(({value}) => value !== 0)
        .map(({entry, value}, index) => ({
            id: `nachtrag17_${meta.sourceReturnID}_${String(index).padStart(3, '0')}`,
            targetType: 'LINE' as const,
            targetID: entry.id,
            action: 'NACHTRAG_17' as const,
            value,
            reason: `${VAT_CORRECTION_COPY.nachtragReason(meta.sourceReturnID)} – ${KIND_LABEL[entry.kind]}: ${(entry.next ?? entry.prev).label}`,
            uid: meta.uid,
            displayName: meta.displayName,
            createdAt: meta.now,
        }));

// --- Versionen / Berichtigung -----------------------------------------------

export const nextVersion = (docs: Array<Pick<VatReturn, 'periodKey' | 'version'>>, periodKey: string): number =>
    docs.filter(doc => doc.periodKey === periodKey).reduce((max, doc) => Math.max(max, doc.version ?? 0), 0) + 1;

/** Eine Berichtigung ist nur in Kraft, wenn ihr Original auf sie zeigt (A3: Rücknahme löscht den Zeiger). */
export const isReturnInForce = (doc: Pick<VatReturn, 'id' | 'korrekturVonID'>, all: Array<Pick<VatReturn, 'id' | 'korrigiertDurchID'>>): boolean =>
    !doc.korrekturVonID || all.some(other => other.id === doc.korrekturVonID && other.korrigiertDurchID === doc.id);

export const activeReturnForPeriod = (docs: VatReturn[], periodKey: string): VatReturn | null =>
    docs
        .filter(doc => doc.periodKey === periodKey && isReturnInForce(doc, docs))
        .sort((a, b) => b.version - a.version)[0] ?? null;

export const needsChangeBanner = (
    doc: Pick<VatReturn, 'status' | 'inputHash' | 'bestaetigterInputHash'> | null | undefined,
    liveHash: string | null
): boolean =>
    !!doc && !!liveHash && !!doc.inputHash
    && (doc.status === 'GEPRUEFT' || doc.status === 'EXPORTIERT')
    && liveHash !== doc.inputHash
    && liveHash !== doc.bestaetigterInputHash;

// --- Zeitraum-Helfer ---------------------------------------------------------

export const periodLabel = (year: number, periodKey: string): string => {
    const parsed = parsePeriodKey(periodKey);
    if (!parsed) {
        return `${periodKey} ${year}`;
    }
    return parsed.type === 'MONAT' ? monthLabel(`${year}-${String(parsed.index).padStart(2, '0')}`) : `${parsed.index}. Quartal ${year}`;
};

export const currentPeriod = (now: Date, periodType: VatPeriodType): { year: number; periodKey: string } =>
    ({year: now.getFullYear(), periodKey: periodKeyForMonth(now.getMonth() + 1, periodType)});

export const prevPeriod = (year: number, periodKey: string): { year: number; periodKey: string } => {
    const parsed = parsePeriodKey(periodKey);
    if (!parsed) {
        return {year, periodKey};
    }
    if (parsed.type === 'MONAT') {
        return parsed.index === 1 ? {year: year - 1, periodKey: 'M12'} : {year, periodKey: `M${String(parsed.index - 1).padStart(2, '0')}`};
    }
    return parsed.index === 1 ? {year: year - 1, periodKey: 'Q4'} : {year, periodKey: `Q${parsed.index - 1}`};
};

/** Dotted key nur für Firestore-sichere Segmente; sonst muss die ganze Map geschrieben werden. */
export const checklistFieldPath = (issueID: string): string | null =>
    /^[A-Za-z0-9_-]+$/.test(issueID) ? `checklist.${issueID}` : null;

// --- Patches -----------------------------------------------------------------

export interface ReleasePatchParams {
    result: VatEngineResult;
    profile: LandlordTaxProfile;
    uid: string;
    displayName: string;
    now: Date;
    bestaetigung: string;
}

export const releasePatch = (p: ReleasePatchParams): Partial<VatReturn> => ({
    status: 'GEPRUEFT',
    kennzahlen: p.result.kennzahlen,
    totals: p.result.stats,
    taxProfileSnapshot: p.profile,
    inputHash: p.result.inputHash,
    bestaetigterInputHash: null,
    computedAt: p.now,
    engineVersion: p.result.engineVersion,
    freigabe: {uid: p.uid, displayName: p.displayName, at: p.now, bestaetigung: p.bestaetigung},
});

export const revokePatch = (): Partial<VatReturn> => ({
    status: 'ENTWURF',
    freigabe: null,
    kennzahlen: null,
    totals: null,
    taxProfileSnapshot: null,
    inputHash: null,
    bestaetigterInputHash: null,
    computedAt: null,
});

// --- Activities --------------------------------------------------------------

export const VAT_ACTIVITY_ICONS = {
    released: 'heroicons_outline:check-circle',
    revoked: 'heroicons_outline:refresh',
    exported: 'heroicons_outline:document-download',
    transmitted: 'heroicons_outline:shield-check',
    correction: 'heroicons_outline:pencil',
    reverted: 'heroicons_outline:refresh',
    nachtrag: 'heroicons_outline:scale',
    discarded: 'heroicons_outline:ban',
    acknowledged: 'heroicons_outline:badge-check',
} as const;

export interface VatActivityParams {
    id: string;
    uid: string;
    displayName: string;
    description: string;
    icon: string;
    returnID: string;
    landlordID: string;
    landlordsName: string | null;
    customerID: string | null;
    now: Date;
    extraContent?: string | null;
}

export const buildVatActivity = (p: VatActivityParams): Activity => ({
    id: p.id,
    createdAt: p.now,
    date: p.now,
    uid: p.uid,
    user: p.displayName,
    icon: p.icon,
    description: `<strong>${p.displayName}</strong> ${p.description}`,
    extraContent: p.extraContent ?? null,
    link: `/umsatzsteuer/${p.landlordID}/voranmeldung/${p.returnID}`,
    useRouter: true,
    customerID: p.customerID ?? null,
    landlordID: p.landlordID,
    landlordsName: p.landlordsName ?? null,
    vatReturnID: p.returnID,
    featureType: ActivityFeatureOption.VAT_RETURN,
});
